import {
  Button,
  Divider,
  HStack,
  Icon,
  IconButton,
  Text,
  Textarea,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import PulseIcon from "components/PulseIcon";
import { appDisplayName } from "modules/apps/tenants";
import { format, isAfter } from "date-fns";
import { useState } from "react";
import { useMemberAuthStore } from "modules/members/auth";
import { FiArrowLeft, FiCheck, FiCheckCircle, FiXCircle } from "react-icons/fi";
import { useParams } from "react-router-dom";
import { MF_ACCESS } from "state/members/access_v2/catalogs";
import PageSpinner from "ui/PageSpinner";

const ExtensionRequest = () => {
  const { id } = useParams();
  const extension = MF_ACCESS.useGetExtensionRequest(id);
  const store = useMemberAuthStore();
  const { mutate: decide, isLoading } = MF_ACCESS.useDecideExtension(
    id as string,
  );
  const { isOpen: approveOpen, onToggle: toggleApprove } = useDisclosure();
  const { isOpen: denyOpen, onToggle: toggleDeny } = useDisclosure();
  const toast = useToast();
  const [reason, setReason] = useState("");

  const requiresComment = Boolean(
    extension?.selectableResource?.requiresComment,
  );
  // Only extends are gated — expiring never needs a reason (server agrees).
  const extendBlocked = requiresComment && reason.trim() === "";
  const resourceName = extension?.selectableResource
    ? appDisplayName(extension.selectableResource)
    : undefined;

  const onDecide = (decision: "extend" | "expire") => {
    extension &&
      decide({
        selectableResourceAccessId: extension.id,
        result: decision,
        comment: decision === "extend" ? reason.trim() || undefined : undefined,
      }).then(() =>
        toast({
          status: "success",
          variant: "subtle",
          title: "Response submitted",
        }),
      );
  };

  if (!extension && store.accessToken) {
    return (
      <VStack
        pb="200px"
        w="full"
        minH="100vh"
        bg="gray.50"
        pt={["50px", "100px"]}
        spacing={10}
      >
        <Text variant="xs.d.sb" textAlign="center">
          Request not found
        </Text>
        <Text variant="lil" textAlign="center">
          The request you're looking for no longer exists
        </Text>
      </VStack>
    );
  } else if (!store.accessToken) {
    return <PageSpinner />;
  } else if (!extension) {
    return null;
  }

  return (
    <VStack pb="200px" w="full" minH="100vh" bg="gray.50" spacing={10}>
      <VStack
        bg="white"
        borderRadius="8px"
        border="1px solid"
        borderColor="gray.200"
        shadow="lg"
        align="flex-start"
        w="full"
        p={[4, 8]}
        maxW={["full", "600px"]}
        spacing={8}
      >
        <HStack w="full" align="flex-start" spacing={4}>
          <PulseIcon icon={FiCheck} size="48px" />
          <VStack align="flex-start" spacing={1}>
            <HStack spacing={4}>
              <Text variant="lg.sb">Extend Access</Text>
            </HStack>
            <Text fontSize="sm" color="gray.600">
              Your access to {resourceName} is set to
              expire soon. Do you want to extend your access?
            </Text>
          </VStack>
        </HStack>
        <Divider />
        <HStack w="full" align="flex-start" justify="space-between">
          <VStack align="flex-start" spacing={1}>
            <Text variant="label">Resource</Text>
            <Text variant="lil">{resourceName}</Text>
          </VStack>
          <VStack align="flex-start" spacing={1}>
            <Text variant="label">Current expiry</Text>
            <Text variant="lil">
              {extension?.expiresAt &&
                format(new Date(extension?.expiresAt), "yyyy-MM-dd hh:mm")}
            </Text>
          </VStack>
          <VStack align="flex-start" spacing={1}>
            <Text variant="label">Extend until</Text>
            <Text variant="lil">
              {extension.canExtend
                ? extension?.extendUntil &&
                  format(new Date(extension?.extendUntil), "yyyy-MM-dd hh:mm")
                : "Unavailable"}
            </Text>
          </VStack>
        </HStack>
        <Divider />
        {extension.canExtend ? (
          <VStack w="full" align="stretch" spacing={4}>
            {approveOpen && (
              <VStack w="full" align="flex-start" spacing={1}>
                <Text variant="label">
                  Reason{requiresComment ? "" : " (optional)"}
                </Text>
                <Textarea
                  size="sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  isInvalid={extendBlocked}
                  placeholder={
                    requiresComment
                      ? "A reason is required to extend access"
                      : "Add a reason (optional)"
                  }
                />
                {extendBlocked && (
                  <Text fontSize="xs" color="red.500">
                    A reason is required to extend access.
                  </Text>
                )}
              </VStack>
            )}
            <HStack w="full" justify="flex-end">
              {!denyOpen && !approveOpen ? (
                <>
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiXCircle} />}
                    colorScheme="red"
                    onClick={toggleDeny}
                  >
                    Expire
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiCheckCircle} />}
                    colorScheme="green"
                    onClick={toggleApprove}
                  >
                    Extend
                  </Button>
                </>
              ) : denyOpen ? (
                <>
                  <IconButton
                    variant={"secondary"}
                    size="sm"
                    icon={<Icon as={FiArrowLeft} />}
                    aria-label="back"
                    onClick={toggleDeny}
                  />
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiXCircle} />}
                    colorScheme="red"
                    isLoading={isLoading}
                    onClick={() => onDecide("expire")}
                  >
                    Confirm expire
                  </Button>
                </>
              ) : (
                <>
                  <IconButton
                    variant={"secondary"}
                    size="sm"
                    icon={<Icon as={FiArrowLeft} />}
                    aria-label="back"
                    onClick={toggleApprove}
                  />
                  <Button
                    size="sm"
                    leftIcon={<Icon as={FiCheckCircle} />}
                    colorScheme="green"
                    onClick={() => onDecide("extend")}
                    isLoading={isLoading}
                    isDisabled={extendBlocked}
                  >
                    Confirm Extend
                  </Button>
                </>
              )}
            </HStack>
          </VStack>
        ) : (
          <HStack w="full" justify="flex-end">
            <Text variant="lil">
              This resource has already been extended or cannot be extended.
            </Text>
          </HStack>
        )}
      </VStack>
    </VStack>
  );
};

export default ExtensionRequest;
